// Inline "schedule a meeting" card for related-list panels.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";

interface Props {
  contactId?: string;
  accountId?: string;
  dealId?: string;
  campaignId?: string;
  invalidateQueryKeys?: unknown[][];
}

export function QuickAddMeeting({ contactId, accountId, dealId, campaignId, invalidateQueryKeys = [] }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [venue, setVenue] = useState("Microsoft Teams");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      await api.post("/meetings", {
        subject, from, to: to || undefined, venue,
        contactId, accountId, dealId, campaignId,
      });
    },
    onSuccess: () => {
      setSubject(""); setFrom(""); setTo(""); setOpen(false); setError(null);
      invalidateQueryKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Failed to create meeting."),
  });

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="mr-1 h-4 w-4"/>Schedule meeting
      </Button>
    );
  }

  return (
    <div className="rounded-md border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium">New meeting</div>
        <button onClick={() => setOpen(false)} aria-label="Close" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4"/>
        </button>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <Input placeholder="Title" value={subject} onChange={(e) => setSubject(e.target.value)} className="md:col-span-2" />
        <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        <Select value={venue} onChange={(e) => setVenue(e.target.value)}>
          <option value="Microsoft Teams">Microsoft Teams</option>
          <option value="Zoom">Zoom</option>
          <option value="Google Meet">Google Meet</option>
          <option value="Client location">Client location</option>
          <option value="Our office">Our office</option>
          <option value="Phone">Phone</option>
        </Select>
        <Button onClick={() => mut.mutate()} disabled={!subject || !from || mut.isPending}>
          {mut.isPending ? "Saving…" : "Add"}
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
